import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import './PublicOnboardingPages.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD
  || process.env.REACT_APP_BACKEND_URL
  || 'http://localhost:8000';

const PublicExternalTaskCompletion = () => {
  useUiLanguage();
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [completionNote, setCompletionNote] = useState('');
  const [taskData, setTaskData] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [hireInfo, setHireInfo] = useState(null);
  const [assigneeEmail, setAssigneeEmail] = useState('');
  const [completed, setCompleted] = useState(false);

  const resolveToken = useCallback(async () => {
    const response = await fetch(`${BACKEND_URL}/onboarding/public/resolve-external-task-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload?.success) {
      throw new Error(payload?.error || 'Invalid or expired completion link');
    }
    return payload;
  }, [token]);

  useEffect(() => {
    const fetchTask = async () => {
      try {
        if (!token) throw new Error('Missing completion token');
        const payload = await resolveToken();
        setTaskData(payload.task || null);
        setCompanyInfo(payload.company || null);
        setHireInfo(payload.hire || null);
        setAssigneeEmail(payload.assigneeEmail || '');
        if ((payload.task?.status || '').toLowerCase() === 'completed') {
          setCompleted(true);
        }
      } catch (err) {
        console.error(err);
        setError(err.message || 'Unable to load completion request');
      } finally {
        setLoading(false);
      }
    };

    fetchTask();
  }, [resolveToken, token]);

  const handleComplete = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const response = await fetch(`${BACKEND_URL}/onboarding/public/complete-external-task`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          note: completionNote.trim() || undefined
        })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.error || 'Could not complete task');
      }
      setCompleted(true);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not complete task');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="public-page">
        <div className="public-page-shell">
          <div className="public-card public-status-card">
            <div className="public-status-icon loading">
              <i className="fa-solid fa-spinner fa-spin" />
            </div>
            <h2 className="public-title">{uiText("Loading Task Details")}</h2>
            <p className="public-subtitle">{uiText("Please wait while we verify your secure link.")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-page">
        <div className="public-page-shell">
          <div className="public-card public-status-card">
            <div className="public-status-icon error">
              <i className="fa-solid fa-circle-exclamation" />
            </div>
            <h2 className="public-title">{uiText("Link Error")}</h2>
            <p className="public-subtitle">{uiText(error)}</p>
          </div>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className="public-page">
        <div className="public-page-shell">
          <div className="public-card public-status-card">
            <div className="public-status-icon success">
              <i className="fa-solid fa-circle-check" />
            </div>
            <h2 className="public-title">{uiText("Task Completed")}</h2>
            <p className="public-subtitle">{uiText("This onboarding task has been marked as complete.")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="public-page">
      <div className="public-page-shell">
        <div className="public-card">
        <div className="form-header">
            {companyInfo?.companyName && (
              <div className="public-company">
                <i className="fa-solid fa-building" />
                {companyInfo.companyName}
              </div>
            )}
            <h1 className="public-title">{taskData?.title || uiText("Complete Task")}</h1>
            <p className="public-subtitle">{taskData?.description || uiText("Confirm completion once the requested action has been finished.")}</p>
        </div>

          <div className="public-meta-grid">
            <div className="public-meta-item">
              <div className="public-meta-label">{uiText("New Hire")}</div>
              <div className="public-meta-value">{hireInfo?.name || hireInfo?.email || uiText("New hire")}</div>
            </div>
            <div className="public-meta-item">
              <div className="public-meta-label">{uiText("Assignee")}</div>
              <div className="public-meta-value">{assigneeEmail || uiText("External assignee")}</div>
            </div>
            <div className="public-meta-item">
              <div className="public-meta-label">{uiText("Due Date")}</div>
              <div className="public-meta-value">{taskData?.dueDate || uiText("Not set")}</div>
            </div>
          </div>

          <form className="public-form" onSubmit={handleComplete}>
            <div className="public-form-group">
              <label className="public-form-label">{uiText("Completion Note (optional)")}</label>
              <textarea
                className="public-textarea"
                value={completionNote}
                onChange={(event) => setCompletionNote(event.target.value)}
                rows={4}
                placeholder={uiText("Add a short note about what was completed.")}
              />
            </div>

            {error && <p className="public-error">{uiText(error)}</p>}

            <button type="submit" className="public-submit" disabled={submitting}>
              {submitting ? uiText("Submitting...") : uiText("Mark Task Complete")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PublicExternalTaskCompletion;
